import { useState } from 'react';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function BHRTHormoneTherapyPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    practiceName: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('https://readdy.ai/api/form/d62jacgr3m6fkobkntv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', practiceName: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <i className="ri-heart-pulse-line text-2xl text-purple-600"></i>
              <span className="text-xl font-bold bg-gradient-to-r from-purple-600 to-purple-400 bg-clip-text text-transparent">
                MillionDollarMed
              </span>
            </a>
            <div className="hidden md:flex items-center gap-8">
              <a href="/#services" className="text-gray-700 hover:text-purple-600 transition-colors text-sm font-medium">Services</a>
              <a href="/#industries" className="text-gray-700 hover:text-purple-600 transition-colors text-sm font-medium">Industries</a>
              <a href="/automation" className="text-gray-700 hover:text-purple-600 transition-colors text-sm font-medium">Automation</a>
              <a href="/about" className="text-gray-700 hover:text-purple-600 transition-colors text-sm font-medium">About</a>
              <a href="/book-strategy-call" className="px-5 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all whitespace-nowrap">
                Book Strategy Call
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-purple-100 rounded-full mb-6">
              <span className="text-purple-700 font-semibold text-sm">BHRT & Hormone Therapy Marketing</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Fill Your <span className="bg-gradient-to-r from-purple-600 to-purple-400 bg-clip-text text-transparent">Hormone Therapy Clinic</span> with Qualified Patients
            </h1>
            <p className="text-xl text-gray-600 mb-10 leading-relaxed">
              HIPAA-compliant digital marketing and automated patient acquisition for BHRT clinics and hormone therapy centers seeking patients ready to optimize their hormonal health.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact-form" className="px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-semibold rounded-lg hover:shadow-xl transition-all whitespace-nowrap">
                Get Your Growth Plan
              </a>
              <a href="#solutions" className="px-8 py-4 bg-white text-purple-600 font-semibold rounded-lg border-2 border-purple-600 hover:bg-purple-50 transition-all whitespace-nowrap">
                See How We Help
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Marketing Challenges for BHRT & Hormone Therapy Clinics
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the unique obstacles hormone therapy providers face
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-user-search-line',
                title: 'Finding Qualified Patients',
                description: 'Attracting patients who understand hormone therapy and are ready to commit to treatment'
              },
              {
                icon: 'ri-question-line',
                title: 'Educating About BHRT Benefits',
                description: 'Helping prospects understand bioidentical hormones and how they differ from synthetic options'
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Justifying Out-of-Pocket Costs',
                description: 'Converting prospects willing to pay cash for hormone optimization services'
              },
              {
                icon: 'ri-team-line',
                title: 'Standing Out from Competitors',
                description: 'Differentiating your clinic in a growing hormone therapy marketplace'
              },
              {
                icon: 'ri-shield-cross-line',
                title: 'HIPAA-Compliant Marketing',
                description: 'Running effective digital campaigns while protecting patient privacy and sensitive health data'
              },
              {
                icon: 'ri-calendar-line',
                title: 'Maintaining Consistent Patient Flow',
                description: 'Keeping your schedule full with new consultations and ongoing hormone therapy patients'
              }
            ].map((challenge, index) => (
              <div key={index} className="p-8 bg-gray-50 rounded-xl hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-purple-400 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${challenge.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{challenge.title}</h3>
                <p className="text-gray-600 leading-relaxed">{challenge.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-20 px-6 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Marketing Automation Platform Fills Your BHRT Clinic Automatically
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We help BHRT and hormone therapy clinics attract qualified patients through HIPAA-compliant digital marketing and automated systems that educate prospects about hormone optimization and convert them into committed patients.
              </p>
              <div className="space-y-6">
                {[
                  'Automated educational campaigns explaining BHRT benefits and treatment protocols',
                  'Lead nurturing sequences for prospects researching hormone therapy options',
                  'Patient intake and consultation booking workflows',
                  'CRM designed specifically for hormone therapy patient journeys',
                  'No missed leads - every inquiry receives immediate follow-up',
                  'HIPAA-conscious implementation with secure patient data handling'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-gradient-to-br from-purple-600 to-purple-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-gray-700 font-medium">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/7739a4a1bd1704980675dcd2d85289be.jpg"
                alt="BHRT Hormone Therapy Clinic"
                className="rounded-2xl shadow-2xl w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Healthcare Marketing Services for Hormone Therapy Clinics
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive digital marketing solutions designed for BHRT and hormone therapy practices
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Medical Website Design', icon: 'ri-layout-line', link: '/services/medical-website-design' },
              { title: 'Medical SEO & Local SEO', icon: 'ri-search-line', link: '/services/medical-seo' },
              { title: 'Healthcare PPC Advertising', icon: 'ri-advertisement-line', link: '/services/healthcare-ppc' },
              { title: 'Content Marketing', icon: 'ri-article-line', link: '/services/content-marketing' },
              { title: 'Marketing Automation', icon: 'ri-robot-line', link: '/services/marketing-automation' },
              { title: 'AI Lead Capture', icon: 'ri-customer-service-line', link: '/services/ai-lead-capture' }
            ].map((service, index) => (
              <a 
                key={index}
                href={service.link}
                className="p-8 bg-gray-50 rounded-xl hover:shadow-xl transition-all group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-purple-400 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-purple-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Leading Hormone Therapy Providers
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Dr. Jennifer Walsh',
                title: 'BHRT Clinic Medical Director',
                image: 'https://readdy.ai/api/search-image?query=professional%20female%20doctor%20hormone%20therapy%20specialist%20confident%20smile%20medical%20director%20modern%20clinic%20trustworthy%20healthcare%20provider&width=150&height=150&seq=bhrt-test-1&orientation=squarish',
                text: 'MillionDollarMed transformed our patient acquisition. The automated system educates prospects about BHRT while we focus on patient care. New consultations increased 280% in 7 months.'
              },
              {
                name: 'Dr. Michael Torres',
                title: 'Hormone Optimization Specialist',
                image: 'https://readdy.ai/api/search-image?query=professional%20male%20hispanic%20doctor%20hormone%20specialist%20confident%20expression%20medical%20attire%20modern%20healthcare%20facility&width=150&height=150&seq=bhrt-test-2&orientation=squarish',
                text: 'The lead nurturing campaigns are brilliant. Prospects arrive at consultations already educated about hormone therapy and ready to start treatment. Our conversion rate has never been higher.'
              },
              {
                name: 'Dr. Lisa Anderson',
                title: 'Women\'s Hormone Health Clinic Owner',
                image: 'https://readdy.ai/api/search-image?query=professional%20female%20doctor%20womens%20health%20hormone%20specialist%20warm%20approachable%20wellness%20clinic%20compassionate%20provider&width=150&height=150&seq=bhrt-test-3&orientation=squarish',
                text: 'Their HIPAA-compliant marketing strategies and automation platform keep our schedule consistently full with qualified patients. We finally have predictable growth and no missed opportunities.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="p-8 bg-white rounded-xl shadow-lg">
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover object-top"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.title}</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Book Your Free Strategy Call
            </h2>
            <p className="text-xl text-gray-600">
              Let's discuss how we can help your hormone therapy clinic attract more qualified patients
            </p>
          </div>
          <form onSubmit={handleSubmit} className="bg-gray-50 p-8 rounded-2xl" data-readdy-form id="bhrt-hormone-therapy-strategy">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Practice Name *</label>
                <input
                  type="text"
                  name="practiceName"
                  value={formData.practiceName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tell Us About Your Practice</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
                placeholder="What are your main marketing challenges?"
              ></textarea>
              <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
            </div>
            <button
              type="submit"
              className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-semibold rounded-lg hover:shadow-xl transition-all whitespace-nowrap"
            >
              Book My Strategy Call
            </button>
            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-medium">Thank you! We'll contact you within 24 hours to schedule your strategy call.</p>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium">Something went wrong. Please try again or email us directly.</p>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-4 text-center">
              Your information is protected and will be handled in accordance with HIPAA guidelines.
            </p>
          </form>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-r from-[#0D9488] to-[#14B8A6]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Grow Your Hormone Therapy Clinic?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Let's build a HIPAA-compliant marketing system that attracts qualified patients automatically
          </p>
          <a href="#contact-form" className="inline-block px-8 py-4 bg-white text-[#0D9488] font-semibold rounded-lg hover:shadow-2xl transition-all whitespace-nowrap">
            Schedule Your Free Consultation
          </a>
        </div>
      </section>

      <RelatedServices />
      <RelatedIndustries currentSlug="bhrt-hormone-therapy" />

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-heart-pulse-line text-2xl text-purple-400"></i>
                <span className="text-xl font-bold">MillionDollarMed</span>
              </div>
              <p className="text-gray-400 text-sm">
                HIPAA-compliant digital marketing for healthcare providers
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm">Services</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/services/medical-website-design" className="text-gray-400 hover:text-purple-400 transition-colors">Website Design</a></li>
                <li><a href="/services/medical-seo" className="text-gray-400 hover:text-purple-400 transition-colors">Medical SEO</a></li>
                <li><a href="/services/healthcare-ppc" className="text-gray-400 hover:text-purple-400 transition-colors">Healthcare PPC</a></li>
                <li><a href="/services/marketing-automation" className="text-gray-400 hover:text-purple-400 transition-colors">Marketing Automation</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm">Industries</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/industries/obgyn-menopause" className="text-gray-400 hover:text-purple-400 transition-colors">OB-GYN & Menopause</a></li>
                <li><a href="/industries/preventive-medicine" className="text-gray-400 hover:text-purple-400 transition-colors">Preventive Medicine</a></li>
                <li><a href="/industries/bhrt-hormone-therapy" className="text-gray-400 hover:text-purple-400 transition-colors">BHRT & Hormone Therapy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/about" className="text-gray-400 hover:text-purple-400 transition-colors">About Us</a></li>
                <li><a href="/automation" className="text-gray-400 hover:text-purple-400 transition-colors">Automation Platform</a></li>
                <li><a href="/book-strategy-call" className="text-gray-400 hover:text-purple-400 transition-colors">Book Strategy Call</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 MillionDollarMed. All rights reserved. | HIPAA-Compliant Marketing Solutions</p>
          </div>
        </div>
      </footer>
    </div>
  );
}